import ShapeProgressBar from './ShapeProgressBar';
import Color from 'color';
import GenericListTool from '../tools/GenericListTool';
import { type TextProps } from '../objects/EmbeddedTextBehaviorsV2';
import { camelToSentenceCase } from '../utils/string';
import shapeMilestone from '../assets/icons/shapeMilestone.svg';
import { svgToIcon } from '../utils/svg';
import type Shape from '../objects/Shape';

class ShapeMilestone extends ShapeProgressBar {
  textProps: TextProps = {
    allowedStyles: 'any',
    location: 'user',
    textAlign: 'shape',
    verticalAlign: 'shape',
    fontSize: 'user',
    overflow: 'visible',
    wrapText: 'growText',
    margins: 'noScale',
  };

  static VARIATION_DIAMOND = 'diamond';
  static VARIATION_DISC = 'disc';
  static VARIATION_RIGHT_TRIANGLE = 'rightTriangle';
  static VARIATION_STAR = 'star';

  static renderIcon() {
    return svgToIcon(shapeMilestone);
  }

  static defaultWidth() {
    return 20;
  }

  static defaultHeight() {
    return 20;
  }

  static shapeControls = this.createDefaultControls({ noRotate: true });

  static milestoneChoices = [
    {
      value: ShapeMilestone.VARIATION_DIAMOND,
      iconClass: 'fa-solid fa-diamond',
    },
    { value: ShapeMilestone.VARIATION_DISC, iconClass: 'fa-solid fa-circle' },
    {
      value: ShapeMilestone.VARIATION_RIGHT_TRIANGLE,
      iconClass: 'fa-solid fa-play',
    },
    { value: ShapeMilestone.VARIATION_STAR, iconClass: 'fa-solid fa-star' },
  ].map(choice => ({
    ...choice,
    toolName: camelToSentenceCase(choice.value),
  }));

  static toolVariation = GenericListTool({
    toolName: 'Variation',
    attribute: 'variation',
    iconClass: 'fa-solid fa-diamond',
    choices: this.milestoneChoices,
  });

  tools() {
    const progressBarTools = super.tools();

    // Super handles showing the label tool, so just slice in the milestone variation tool.
    return [[ShapeMilestone.toolVariation], ...progressBarTools.slice(1)];
  }

  constructor(fabricObject: Shape) {
    super(fabricObject);

    fabricObject.uniformScaling = true;
  }

  shapeTextAlign() {
    switch (this.textLocation) {
      case 'left':
        return 'right';
      case 'right':
        return 'left';
      case 'below':
      case 'above':
      case 'inside':
      default:
        return 'center';
    }
  }

  render(ctx: CanvasRenderingContext2D) {
    const w = this.width;
    const h = this.height;
    const lighterFillColor = Color(this.fill ?? undefined)
      .mix(Color('white'), 0.5)
      .hex();

    const drawOutline = () => {
      ctx.beginPath();
      switch (this.variation || ShapeMilestone.VARIATION_DIAMOND) {
        case ShapeMilestone.VARIATION_DIAMOND:
          ctx.moveTo(0, -h / 2);
          ctx.lineTo(w / 2, 0);
          ctx.lineTo(0, h / 2);
          ctx.lineTo(-w / 2, 0);
          ctx.lineTo(0, -h / 2);
          break;

        case ShapeMilestone.VARIATION_DISC:
          ctx.ellipse(0, 0, w / 2, h / 2, 0, 0, 2 * Math.PI);
          break;

        case ShapeMilestone.VARIATION_RIGHT_TRIANGLE:
          ctx.moveTo(-w / 4, -h / 2);
          ctx.lineTo(w / 4, 0);
          ctx.lineTo(-w / 4, h / 2);
          break;

        case ShapeMilestone.VARIATION_STAR: {
          // Draw a star.
          const points = 5;
          const outerRadius = w / 2;
          const innerRadius = outerRadius / 2.5;
          const angle = (Math.PI * 2) / (2 * points);
          ctx.moveTo(0, -outerRadius);
          for (let i = 0; i < points; i++) {
            ctx.rotate(angle);
            ctx.lineTo(0, -innerRadius);
            ctx.rotate(angle);
            ctx.lineTo(0, -outerRadius);
          }
          break;
        }
      }
      ctx.closePath();
    };

    if (this.canvas.getZoom() > 0.5) {
      // Draw the shadow
      ctx.save();
      drawOutline();
      ctx.shadowBlur = 8;
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      this._renderStroke(ctx);
      ctx.restore();
    }

    // Draw the fill.
    ctx.save();
    drawOutline();
    ctx.fillStyle = lighterFillColor;
    ctx.fill();
    ctx.restore();

    // Draw the outline.
    ctx.save();
    drawOutline();
    this.stroke = this.fill;
    this._renderStroke(ctx);
    ctx.restore();
  }
}

export default ShapeMilestone;
